/**
 * GlobalLoadingOverlay - Full-screen overlay for background operations
 * Shows on top of everything when API calls are in progress
 */

import { Box, Typography, keyframes } from "@mui/material";
import { QUIZ } from "@/theme/quizTokens";

// Animation keyframes
const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.08);
    opacity: 0.85;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const pawStep = keyframes`
  0% {
    opacity: 0.3;
    transform: scale(0.85) translateY(0);
  }
  50% {
    opacity: 1;
    transform: scale(1) translateY(-4px);
  }
  100% {
    opacity: 0.3;
    transform: scale(0.85) translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(200%);
  }
`;

// Paw print SVG path
const PAW_PATH = "M12 2C9.5 2 7.5 4 7.5 6.5C7.5 9 9.5 11 12 11C14.5 11 16.5 9 16.5 6.5C16.5 4 14.5 2 12 2ZM4.5 9C2.5 9 1 10.5 1 12.5C1 14.5 2.5 16 4.5 16C6.5 16 8 14.5 8 12.5C8 10.5 6.5 9 4.5 9ZM19.5 9C17.5 9 16 10.5 16 12.5C16 14.5 17.5 16 19.5 16C21.5 16 23 14.5 23 12.5C23 10.5 21.5 9 19.5 9ZM6 17C4 17 2.5 18.5 2.5 20.5C2.5 22.5 4 24 6 24C8 24 9.5 22.5 9.5 20.5C9.5 18.5 8 17 6 17ZM18 17C16 17 14.5 18.5 14.5 20.5C14.5 22.5 16 24 18 24C20 24 21.5 22.5 21.5 20.5C21.5 18.5 20 17 18 17Z";

type GlobalLoadingOverlayProps = {
  visible: boolean;
  message?: string | null;
};

export function GlobalLoadingOverlay({ visible, message }: GlobalLoadingOverlayProps) {
  if (!visible) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        animation: `${fadeIn} 0.2s ease-out`,
      }}
    >
      {/* Backdrop with blur */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "rgba(250, 245, 235, 0.92)",
          backdropFilter: "blur(8px)",
        }}
      />

      {/* Content */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
          p: 4,
          maxWidth: 320,
          textAlign: "center",
        }}
      >
        {/* Logo with pulse */}
        <Box
          sx={{
            animation: `${pulse} 2s ease-in-out infinite`,
          }}
        >
          <Box
            component="img"
            src="https://cdn.shopify.com/s/files/1/0946/0622/9844/files/Paws_on_Plates_Tertiary_Logo_Brown.svg?v=1744270930"
            alt="Paws on Plates"
            sx={{
              width: 140,
              height: "auto",
            }}
          />
        </Box>

        {/* Animated paw trail */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <Box
              key={i}
              sx={{
                animation: `${pawStep} 1.5s ease-in-out infinite`,
                animationDelay: `${i * 0.2}s`,
              }}
            >
              <svg width={20} height={20} viewBox="0 0 24 24" fill={QUIZ.color.brown}>
                <path d={PAW_PATH} />
              </svg>
            </Box>
          ))}
        </Box>

        {/* Message */}
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 700,
            color: QUIZ.color.brown,
            lineHeight: 1.4,
          }}
        >
          {message || "Working on it..."}
        </Typography>

        {/* Subtext */}
        <Typography
          sx={{
            fontSize: 14,
            color: QUIZ.color.brown,
            opacity: 0.6,
          }}
        >
          This will only take a moment
        </Typography>

        {/* Progress bar */}
        <Box
          sx={{
            width: 120,
            height: 4,
            borderRadius: 2,
            background: "rgba(123, 51, 6, 0.15)",
            overflow: "hidden",
            mt: 1,
          }}
        >
          <Box
            sx={{
              width: "40%",
              height: "100%",
              borderRadius: 2,
              background: "linear-gradient(90deg, #4A3026, #4A3026, #4A3026)",
              animation: `${shimmer} 1.5s ease-in-out infinite`,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
